import React from 'react'
import css from './TrendIndicator.module.scss'
import PropTypes from 'prop-types'
import FlexContainer from '../FlexContainer/FlexContainer.jsx'
import TrendUp01Icon from '../Icons/TrendUp01Icon.jsx'
import TrendDown01Icon from '../Icons/TrendDown01Icon.jsx'
import ArrowRightIcon from '../Icons/ArrowRightIcon.jsx'
import Label from '../Label/Label.jsx'
import { colors } from '../../utils/colors.js'
import {formatCompactNumber} from "../../utils/formatCompactNumber.js";

const TrendIndicator = ({
  trendValue,
  isPercentage = true,
  comparisonText,
  className,
  layout = 'horizontal',
  suffix = '',
  trend = null
}) => {
  const isZeroTrend = trend !== null ? trend === 0 : trendValue === 0
  const isPositiveTrend = trend !== null ? trend === 1 : trendValue > 0
  const isNegativeTrend = trend !== null ? trend === -1 : trendValue < 0

  const trendBgColor = isZeroTrend
    ? colors.warning10
    : (isPositiveTrend ? colors.success10 : colors.danger10)

  const trendColor = isZeroTrend
    ? colors.warning
    : (isPositiveTrend ? colors.success : colors.danger)

  // Absolute value for formatting
  const absValue = Math.abs(trendValue)

  // Format value for display
  const displayValue = isPercentage
    ? `${Math.round(absValue)}%`
    : formatCompactNumber(absValue)

  // Add negative sign if trend is negative and append suffix
  // Only add negative sign if using automatic trend detection (trend prop not provided)
  const signedDisplayValue = (trend === null && isNegativeTrend) ? `-${displayValue}` : displayValue
  const finalDisplayValue = suffix ? `${signedDisplayValue}${suffix}` : signedDisplayValue

  if (layout === 'vertical') {
    return (
      <div className={`${css.trendRow} ${css.vertical} ${className || ''}`}>
        <FlexContainer alignItems="center" gap={5}>
          <Label backgroundColor={trendBgColor} color={trendColor}>
            {finalDisplayValue}
          </Label>
          {isZeroTrend &&
            <ArrowRightIcon width={16} height={16} color={trendColor} />
          }
          {isPositiveTrend && !isZeroTrend &&
            <TrendUp01Icon width={16} height={16} color={trendColor} />
          }
          {isNegativeTrend &&
            <TrendDown01Icon width={16} height={16} color={trendColor} />
          }
        </FlexContainer>
        {comparisonText &&
          <span className={css.comparisonText}>vs {comparisonText}</span>
        }
      </div>
    )
  }

  return (
    <FlexContainer alignItems="center" gap="10px" className={`${css.trendRow} ${className || ''}`}>
      <Label backgroundColor={trendBgColor} color={trendColor}>
        {finalDisplayValue}
      </Label>
      {isZeroTrend &&
        <ArrowRightIcon width={16} height={16} color={trendColor} />
      }
      {isPositiveTrend && !isZeroTrend &&
        <TrendUp01Icon width={16} height={16} color={trendColor} />
      }
      {isNegativeTrend &&
        <TrendDown01Icon width={16} height={16} color={trendColor} />
      }
      {comparisonText && (
        <span className={css.comparisonText}>vs {comparisonText}</span>
      )}
    </FlexContainer>
  )
}

TrendIndicator.propTypes = {
  trendValue: PropTypes.number.isRequired,
  isPercentage: PropTypes.bool,
  comparisonText: PropTypes.string,
  className: PropTypes.string,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  suffix: PropTypes.string,
  trend: PropTypes.oneOf([-1, 0, 1])
}

export default TrendIndicator
